
import { Injectable } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MODAL_PRIMARY_TEXT, UPLOAD_POPUP } from '../my-projects/utility/constants';
import { BaseService } from 'shared-lib';
import { HttpServiceHelper } from 'src/app/types/common/HttpServiceHelper';
import { environment } from 'src/environments/environment';
import { CommonUploadPopupComponent } from './manage-users/common-upload-popup/common-upload-popup.component';
import { saveAs } from 'file-saver';
import { AcknowledgeModalComponent } from './acknowledge-modal/acknowledge-modal.component';
import { catchError, shareReplay, tap } from "rxjs/operators";
import { BehaviorSubject, Observable, Subject, of } from "rxjs";
import {
  ProjectUserResponse,
  ProjectUserData,
  MasterUserRoleResponse,
} from "./utility/interfaces";

@Injectable({
  providedIn: 'root',
})
export class MyProjectsService {
  private userMicroserviceUrl: string;
  private BASE_PROJECT_URL: string;
  private projectUserCache = null;
  private cacheProjectId: number | null = null;
  private isFetchingProjectUsers = false;
  private fetchProjectUsersSubject = new Subject<ProjectUserResponse>();
  public projectUsersSubject = new BehaviorSubject<any>(null);
  masterUserRoleSubject = new BehaviorSubject<any>(null);
  private masterUserRoledata: Observable<MasterUserRoleResponse> | null = null;

  constructor(private baseService: BaseService, private httpService: HttpServiceHelper, private dialog: MatDialog ) {
    this.userMicroserviceUrl = environment.API_MICROSERVICE_URL?.USER;
    this.BASE_PROJECT_URL = environment.API_MICROSERVICE_URL?.PROJECT;
  }

  /**
   * Helper method to get the appropriate API URL based on isMasterProject flag
   */
  private getProjectApiUrl(isMasterProject: boolean): string {
    return isMasterProject 
      ? environment.API_MICROSERVICE_URL.VERSION.V2.PROJECT
      : environment.API_MICROSERVICE_URL.PROJECT;
  }

  getSuperUsers() {
    return this.baseService.get(`/user/superusers`);
  }

  addAminUser(data) {
    return this.baseService.post('/user/add-admin', data);
  }

  addUser(data, isMasterProject:boolean = false) {
    const apiUrl = this.getProjectApiUrl(isMasterProject);
    return this.baseService.post('/project/save-project-users', data, apiUrl);
  }

  getProjectUsers(projectId: any, langId, isMasterProject = false) {
    const apiUrl = this.getProjectApiUrl(isMasterProject);
    return this.baseService.get(`/project/project-users/all?projectId=${projectId}&languageId=${langId}`, apiUrl);
  }

  getRespectiveProjectUsers(projectId: any, langId, isMasterProject = false, customApiUrl: string) {
    const apiVersion = isMasterProject ? 'v2' : 'v1';
    const apiUrl = customApiUrl + `/Project/api/${apiVersion}/project`;
    return this.baseService.get(`/project-users/all?projectId=${projectId}&languageId=${langId}`, apiUrl);
  }

  getProjectInfo(projectId: any,languageId=1, isMasterProject = false) {
    const apiUrl = this.getProjectApiUrl(isMasterProject);
    return this.baseService.get(`/project/` + projectId + `/${languageId}`, apiUrl);

  }

  getPlatformToolStatus(toolName) {
    return this.baseService.get(`/tools/platform/activation-status?toolName=${toolName}`, environment.API_MICROSERVICE_URL.ADMIN);
  }

  getProjectStatus(projectId:any, isMasterProject:boolean = false) {
    const apiUrl = this.getProjectApiUrl(isMasterProject);
    return this.baseService.get(`/project/getProjectExpiryStatus/${projectId}`, apiUrl);
  }

  getMultiReleaseFeatureFlag() {
    return this.baseService.get(`/feature-configuration/configuration/0`, environment.API_MICROSERVICE_URL.ADMIN);
  }

  // getProjectUsers(projectId:any, langId){
  //     const normalizedProjectId = Number(projectId);
  //   if (this.projectUserCache && this.cacheProjectId === normalizedProjectId) {
  //     return of(this.projectUserCache);
  //   }
  //   if (this.isFetchingProjectUsers && this.cacheProjectId === projectId) {
  //     return this.fetchProjectUsersSubject.asObservable();
  //       }
  //   if (this.cacheProjectId !== normalizedProjectId) {
  //     this.projectUserCache = null;
  //       this.cacheProjectId = normalizedProjectId;
  //   }
  //   this.isFetchingProjectUsers = true;
  //     return this.baseService.get(`/project/project-users/all?projectId=${projectId}&languageId=${langId}`, environment.API_MICROSERVICE_URL?.PROJECT)
  //   .pipe(
  //   tap((data) => {
  //   this.projectUserCache = data;
  //   this.fetchProjectUsersSubject.next(data);
  //   this.isFetchingProjectUsers = false;
  //   }),
  //   catchError((error) => {
  //       this.fetchProjectUsersSubject.error(error);
  //       return of(null);
  //   }),
  //   finalize(() => {
  //     this.isFetchingProjectUsers = false;
  //     this.fetchProjectUsersSubject.complete();
  //     this.fetchProjectUsersSubject = new Subject<ProjectUserResponse>();
  //       }),
  //     shareReplay(1)
  //     );
  // }

  getRejectedUser(projectId: any, isMasterProject:boolean = false) {
    const apiUrl = this.getProjectApiUrl(isMasterProject);
    return this.baseService.get('/project/Rejected-Users?projectId=' + projectId, apiUrl);
  }

  deleteProjectUser(options:any,isMasterProject:boolean = false){
    const apiUrl = this.getProjectApiUrl(isMasterProject);
    return this.baseService.deleteByObject('/project/delete-project-user', options, apiUrl);
  }

  updateUserRole(data, isMasterProject:boolean = false) {
    const apiUrl = this.getProjectApiUrl(isMasterProject);
    return this.baseService.put('/project/update-user-role', data, apiUrl);
  }

  deleteAdminUser(adminDetails:any){
    return this.baseService.deleteByObject('/user/delete-admin', adminDetails, this.userMicroserviceUrl);
  }

  softDeleteProject(options:any, BASE_URL=this.BASE_PROJECT_URL){
    return this.baseService.deleteByObject('/project', options,BASE_URL);
  }

  getProjectRoles(langId){
    return this.baseService.get(`/user-management/roles?languageId=${langId}`, this.userMicroserviceUrl);
  }

  getUsersFromGraphApi(value) {
    return this.httpService.httpGraphUserSearch(value);
  }
  getProjectDomains(projectId: any, isMasterProject:boolean = false) {
     const apiUrl = this.getProjectApiUrl(isMasterProject);
    return this.baseService.get(`/project/project-domains?projectId=${projectId}` , apiUrl);
  }

  addExternalUser(data) {
    return this.baseService.post(`/external-user/registerUser`, data, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  downloadUserData(data) {
    return this.baseService.getBlobAsJson(`user/download-users`, data, this.userMicroserviceUrl);
  }

  openUploadPopup(projectId, domainData, featureConfigurationObj, isMasterProject = false) {
    return this.dialog.open(CommonUploadPopupComponent, {
      data: {
        title: UPLOAD_POPUP.TITLE,
        primaryButtonText: UPLOAD_POPUP.CONFIRM_BUTTON,
        secondaryButtonText: UPLOAD_POPUP.CANCEL_BUTTON,
        message:  UPLOAD_POPUP.MESSAGE,
        projectId: projectId,
        domainData: domainData,
        featureConfigurationObj: featureConfigurationObj,
        isMasterProject: isMasterProject
      },
      disableClose: true,
      autoFocus: false,
      width: '25vw',
    });
  }

  uploadFileData(url, file, isMasterProject = false) {
    const apiUrl = this.getProjectApiUrl(isMasterProject);
    return this.baseService.uploadFile(url, file, apiUrl);
  }

  downloadTemplate(userType){
    return this.baseService.getBlobAsJson(`project/templatedownload/${userType}`, null, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  createFileNameAndSave(data) {
    let filename = data.headers
    .get('content-disposition')
    .split('filename=')[1]
    .split(';')[0];
    filename = filename.replaceAll('"', '');
    const type = data.headers.get('content-type');
    const blob = new Blob([data.body], { type });
    saveAs(blob, filename);
  }

  commonErrorSuccessPopup(primaryHeader: string, bodyMessage: string, popupData?: any){
    return this.dialog.open(AcknowledgeModalComponent, {
      width:'600px',
      data: {
        primaryHeader: primaryHeader,
        bodyMessage: bodyMessage,
        popupData: popupData ? popupData : null,
        primaryButton: MODAL_PRIMARY_TEXT,
      },
      disableClose: true,
    })
  }

  /**
   * GetMasterUserRole: Retrieves the master user role data for a specified language.
   *
   * @param langId - language ID
   * @returns An Observable emitting the master user role data.
   */
  getMasterUserRole(langId) {
    if (this.masterUserRoledata === null)
      this.masterUserRoledata = this.baseService
        .get(`/user/access-user-roles?languageId=${langId}`, this.userMicroserviceUrl)
        .pipe(shareReplay(1));

    return this.masterUserRoledata;
  }

  getCanAddExternalUserForProject(projectId){
    return this.baseService.get(`/user/externalUserProjectAccess/${projectId}`, this.userMicroserviceUrl);
  }

  /**
   * SetProjectUsers: Sets the project users data in the BehaviorSubject
   * for reactive consumption across the app.
   *
   * @param data The project users data to set.
   */
  setProjectUsers = (data: ProjectUserData) => this.projectUsersSubject.next(data);

  /**
   * GetProjectUsersObservable: Returns an observable for the project users
   * subject, allowing components to subscribe to project users data changes.
   */
  getProjectUsersObservable = () => this.projectUsersSubject.asObservable();

  /**
   * GetMasterUserRoleForEnglish : Fetches the master user roles for a given language ID.
   *
   * @param {number} langId - The ID of the language for which to fetch the user roles.
   * @returns {Observable<any>} An observable that emits the fetched user roles data.
   *
   * This method sends a GET request to the user microservice to retrieve the user roles
   * for the specified language ID. The response data is then emitted through the
   * `masterUserRoleSubject`. If an error occurs during the request, the error is emitted
   * through the `masterUserRoleSubject` and the observable returns `null`.
   */
  getMasterUserRoleForEnglish = (langId) =>
    this.baseService
      .get(
        `/user/access-user-roles?languageId=${langId}`,
        this.userMicroserviceUrl
      )
      .pipe(
        tap((data) => {
          this.masterUserRoleSubject.next(data);
        }),
        catchError((error) => {
          this.masterUserRoleSubject.error(error);
          return of(null);
        })
      );
}
